import { DirectiveOptions } from 'vue';
import { TouchDirection, TouchTravelled } from '@a/directive/ui/touchswipe/types';

const useTouchFeatures = (element: HTMLElement, options: {
  onMove: (element: HTMLElement, travelled: TouchTravelled) => void,
  onEnd: (element: HTMLElement, travelled: TouchTravelled) => void
}): TouchModFeature => {
  let start: Touch = null;
  let end: Touch = null;
  let touchDirection: TouchDirection = null;

  const touchStart = (event: TouchEvent) => {
    touchDirection = null;

    start = event.touches[0] || event.changedTouches[0];
  };

  const touchEnd = (event: TouchEvent) => {
    end = event.touches[0] || event.changedTouches[0];
    const distanceTravelled = getDistanceTraveled(end);
    options.onEnd(element, distanceTravelled);
  };

  /**
   * forbids swipe movement to a specific side when a direction is already set
   * @param direction
   * @param travelled
   * @param event
   */
  const processTouch = (
    direction: TouchDirection,
    travelled: TouchTravelled,
    event: TouchEvent,
  ) => {
    switch (direction) {
      case TouchDirection.TOP:
      case TouchDirection.BOTTOM:
        // --- top / bottom swipe detected, preventing mutation of x coordinate
        travelled.x = 0;
        break;

      case TouchDirection.LEFT:
      case TouchDirection.RIGHT:
        // --- left / right swipe detected, preventing mutation of y coordinate
        travelled.y = 0;

        event.preventDefault();
        break;
    }
  };

  const touchMove = (event: TouchEvent) => {
    const move = event.touches[0] || event.changedTouches[0];

    const distanceTraveled = getDistanceTraveled(move);

    if (touchDirection === null && distanceTraveled.direction !== TouchDirection.NO_ACTION) {
      touchDirection = distanceTraveled.direction;
    }

    processTouch(touchDirection, distanceTraveled, event);
    options.onMove(element, distanceTraveled);
  };

  /**
   * returns the distance swiped
   * @param current
   */
  const getDistanceTraveled = (current: Touch): TouchTravelled => {
    const travelled: TouchTravelled = {
      x: start.clientX - current.clientX,
      y: start.clientY - current.clientY,
    };

    return {
      ...travelled,
      direction: getDirection(current, travelled, 6),
    };
  };

  /**
   * detects the direction when {threhold} is exceeded
   * @param current
   * @param travelled
   * @param threshold
   */
  const getDirection = (
    current: Touch,
    travelled: TouchTravelled = undefined,
    threshold = 0,
  ): TouchDirection => {
    if (current.clientX < start.clientX && (!travelled || Math.abs(travelled.x) > threshold)) {
      return TouchDirection.LEFT;
    }

    if (current.clientX > start.clientX && (!travelled || Math.abs(travelled.x) > threshold)) {
      return TouchDirection.RIGHT;
    }

    if (current.clientY < start.clientY && (!travelled || Math.abs(travelled.y) > threshold)) {
      return TouchDirection.BOTTOM;
    }

    if (current.clientY > start.clientY && (!travelled || Math.abs(travelled.y) > threshold)) {
      return TouchDirection.TOP;
    }

    return TouchDirection.NO_ACTION;
  };

  return {
    touchStart, touchEnd, touchMove, getDistanceTraveled, getDirection,
  };
};

interface TouchModFeature {
  touchStart: (event: TouchEvent) => void;
  touchEnd: (event: TouchEvent) => void;
  touchMove: (event: TouchEvent) => void;
  getDirection: (current: Touch, traveled: TouchTravelled, threshold: number) => TouchDirection;
  getDistanceTraveled: (touch: Touch) => TouchTravelled;
}

/**
 * stateful directive
 */
export const TouchSwipe = ((): DirectiveOptions => {
  let feature: TouchModFeature = null;

  return {
    inserted: (el, binding) => {
      if (binding.value.disabled === true) {
        return;
      }

      feature = useTouchFeatures(el, {
        onMove: binding.value.onMove,
        onEnd: binding.value.onEnd,
      });

      el.addEventListener('touchstart', feature.touchStart);
      el.addEventListener('touchend', feature.touchEnd);
      el.addEventListener('touchcancel', feature.touchEnd);
      el.addEventListener('touchmove', feature.touchMove, { passive: false });
    },
    unbind: (el, binding) => {
      if (binding.value.disabled === true) {
        return;
      }

      el.removeEventListener('touchstart', feature.touchStart);
      el.removeEventListener('touchend', feature.touchEnd);
      el.removeEventListener('touchcancel', feature.touchEnd);
      el.removeEventListener('touchmove', feature.touchMove);
    },
  };
});
